import Head from "next/head";
import { useState } from "react";
import { useRouter } from "next/router";
import useSWR from "swr";
import Throw404 from "components/throw404";
import Card from "components/global/card";
import Cover from "components/global/cover";
import Breadcrumb from "components/global/breadcrumbs";
import Attributes from "components/global/attributes";
import EditResortPanel from "components/resorts/edit";
import EditLink from "components/admin/editlink";
import RESORT_QUERY from "queries/resort";

export default function ResortDetails() {
  const router = useRouter();
  const [editOpen, setEditOpen] = useState(false);

  const variables = {
    slug: router.query.resort,
  };

  const { data, error } = useSWR([RESORT_QUERY, variables]);

  if (error) {
    console.log(error);
  }

  if (!Array.isArray(data?.resorts) || !data?.resorts?.length) {
    return <Throw404 />;
  }

  const resort = data.resorts[0];

  const description = `Information on rides, shows and other attractions found at ${resort.Name}`;

  let CoverPhoto = "";
  let ResortTitle = "";
  if (resort.CoverPhoto || resort.MainPhoto) {
    CoverPhoto = (
      <Cover
        title={resort.Name}
        image={resort.CoverPhoto || resort.MainPhoto}
      />
    );
  } else {
    ResortTitle = (
      <h2 className="mb-10 font-serif text-5xl text-gray-800">{resort.Name}</h2>
    );
  }

  const attributes = [];

  // Todo handle these better, yuck
  if (resort.CurrentlyOperating) {
    attributes.push({
      ValueType: "Status",
      Value: resort.CurrentlyOperating ? "Operational" : "Closed",
    });
  }
  if (resort.OpeningDate) {
    attributes.push({
      ValueType: "Opening Date",
      Value: resort.OpeningDate,
    });
  }

  if (resort.ClosedDate) {
    attributes.push({
      ValueType: "Date Closed",
      Value: resort.ClosedDate,
    });
  }

  const breadcrumbs = [];

  if (Array.isArray(resort.Companies) && resort.Companies.length > 0) {
    const company = resort.Companies[0];
    breadcrumbs.push({
      name: company.Name,
      link: "/company/[company]",
      linkAs: `/company/${company.Slug}`,
    });
  }

  breadcrumbs.push({
    name: resort.Name,
    link: "/resort/[resort]",
    linkAs: `/resort/${resort.Slug}`,
  });

  return (
    <>
      <Head>
        <title>{resort.Name} | Open Coaster</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={resort.Name} />
        <meta property="og:description" content={description} />
        <meta
          property="og:url"
          content={`https://opencoaster.com/resort/${resort.Slug}`}
        />
        {resort.MainPhoto && (
          <>
            <meta
              property="og:image"
              content={`https://images.opencoaster.com/1200x630/${resort.MainPhoto.hash}${resort.MainPhoto.ext}`}
            />
            <meta
              name="twitter:image"
              content={`https://images.opencoaster.com/1200x600/${resort.MainPhoto.hash}${resort.MainPhoto.ext}`}
            />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta
              property="og:image:alt"
              content={
                resort.MainPhoto.alternativeText
                  ? resort.MainPhoto.alternativeText
                  : resort.Name
              }
            />
            <meta name="twitter:card" content="summary_large_image" />
          </>
        )}
      </Head>
      <EditResortPanel open={editOpen} setOpen={setEditOpen} resort={resort} />
      {CoverPhoto}
      <main className="container m-6 mx-auto px-6 sm:px-0">
        {ResortTitle}
        <Breadcrumb items={breadcrumbs} />
        {Array.isArray(resort.Parks) && resort.Parks.length > 0 && (
          <>
            <h3 className="mb-3 font-serif text-3xl text-gray-800">
              {resort.Parks.length > 1 ? "Parks" : "Park"}
            </h3>
            <div className="mb-10 grid grid-cols-2 gap-8 md:grid-cols-2 lg:grid-cols-4">
              {resort.Parks.map((park) => (
                <Card
                  link={`/park/${park.Slug}`}
                  title={park.Name}
                  image={park.MainPhoto}
                  type="park"
                  isTempClosed={park.IsTempClosed}
                  key={park.id}
                />
              ))}
            </div>
          </>
        )}
        <div className="order-first md:order-last md:col-span-1">
          <ul role="list" className="space-y-3">
            <Attributes attributes={attributes} title="Key Facts" />
            <EditLink setEditOpen={setEditOpen} />
          </ul>
        </div>
      </main>
    </>
  );
}
